import { useUser } from '@/features/Auth/hooks/useUser';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/shadcn/ui/avatar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/shadcn/ui/collapsible"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/shadcn/ui/popover"
import { getInitials } from '@/shared/utils/stringUtils';
import { selectAvailableFacilities } from '@/features/Auth/authSelectors';
import { selectFacility } from '@/features/Auth/authSlice';
import FacilitySwitcherDialog from '@/shared/components/FacilitySwitcherDialog';
import { AuthStoreKeys } from '@/app/constants/storeEnums';
import { secureStorage } from '@/shared/utils/encryption';
import { useNavigationBlock } from '@/shared/contexts/NavigationBlockContext';

interface SidebarProps {
  className?: string;
  onMobileNavigation?: () => void;
  defaultCollapsed?: boolean;
}

interface SubMenuItem {
  icon: string;
  alt: string;
  label: string;
  path: string;
  defaultCollapsed?: boolean;
  alwaysCollapse?: boolean;
}

interface SidebarItem {
  icon: string;
  alt: string;
  label: string;
  path: string;
  defaultCollapsed: boolean;
  alwaysCollapse?: boolean;
  subMenu?: SubMenuItem[];
}

/**
 * Determines if the sidebar should collapse when navigating to a menu item.
 * - alwaysCollapse: true = collapse regardless of screen size
 * - defaultCollapsed: true = collapse only on medium screens (768px - 1280px)
 */
const shouldCollapseOnNavigation = (
  item: { defaultCollapsed?: boolean; alwaysCollapse?: boolean }
): boolean => {
  if (item.alwaysCollapse) {
    return true;
  }
  if (item.defaultCollapsed && window.innerWidth >= 768 && window.innerWidth <= 1280) {
    return true;
  }
  return false;
};

// Sidebar menu items configuration - defined outside component for performance
const sidebarItems: SidebarItem[] = [
  { icon: '/images/sidebar-icons/img_clip_path_group.svg', alt: 'dashboard', label: 'Dashboard', path: '/dashboard', defaultCollapsed: false},
  { icon: '/images/sidebar-icons/img_patient_master.svg', alt: 'patients', label: 'Patient Master', path: '/patient-master', defaultCollapsed: false},
  { icon: '/images/sidebar-icons/img_vector_indigo_400.svg', alt: 'calendar', label: 'Appointments', path: '/appointments', defaultCollapsed: false},
  { 
    icon: '/images/sidebar-icons/img_communication.svg', 
    alt: 'communication', 
    label: 'Communication', 
    path: '/communication', 
    defaultCollapsed: true,
    alwaysCollapse: true,
    subMenu: [
      { icon: '/images/sidebar-icons/envelope.svg', alt: 'messages', label: 'Messages', path: '/communication/messages', alwaysCollapse: true },
      { icon: '/images/sidebar-icons/chat-dots.svg', alt: 'text', label: 'Text', path: '/communication/text', alwaysCollapse: true },
      { icon: '/images/sidebar-icons/clock.svg', alt: 'broadcast', label: 'Broadcast', path: '/communication/broadcast', alwaysCollapse: true }
    ]
  },
  { icon: '/images/sidebar-icons/img_heart_pulse.svg', alt: 'health', label: 'Health Records', path: '/health-records', defaultCollapsed: false},
  { icon: '/images/sidebar-icons/img_credit_card.svg', alt: 'payment-transactions', label: 'Payment Transactions', path: '/payment-transactions', defaultCollapsed: false},
  { 
    icon: '/images/sidebar-icons/configuration-icon.png', 
    alt: 'configurations', 
    label: 'Configurations', 
    path: '/configurations', 
    defaultCollapsed: false,
    subMenu: [
      { icon: '/images/sidebar-icons/img_forms_copy.svg', alt: 'forms', label: 'Forms', path: '/configurations/forms' },
      { icon: '/images/sidebar-icons/img_template_copy_2.svg', alt: 'templates', label: 'Templates', path: '/configurations/templates' },
      { icon: '/images/sidebar-icons/img_reminder_copy_3.svg', alt: 'reminders', label: 'Reminders', path: '/configurations/reminders', defaultCollapsed: true },
      { icon: '/images/sidebar-icons/img_patient_master.svg', alt: 'patient-groups', label: 'Patient Groups', path: '/configurations/patient-groups', defaultCollapsed: true }
    ]
  },
  { icon: '/images/sidebar-icons/img_vector_gray_900_01.svg', alt: 'settings', label: 'Settings', path: '/settings', defaultCollapsed: true, alwaysCollapse: true}
];

const Sidebar: React.FC<SidebarProps> = ({ className = '', onMobileNavigation, defaultCollapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAndNavigate } = useNavigationBlock();
  
  // Initialize state from encrypted localStorage to persist across re-mounts
  const [isOpen, setIsOpen] = useState(() => {
    const saved = secureStorage.getItem<boolean>(AuthStoreKeys.SidebarIsOpen);
    return saved !== null ? saved : true;
  });
  
  
  const [userHasToggled, setUserHasToggled] = useState(() => {
    const saved = secureStorage.getItem<boolean>(AuthStoreKeys.SidebarUserHasToggled);
    return saved !== null ? saved : false;
  });
  
  const [activePath, setActivePath] = useState(window.location.pathname);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const { authUserId, fullName, userTypeLabel, practiceInfo, userProfileImage, twoWayTextEnabled = false } = useUser();
  
  // Facility switcher state
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const availableFacilities = useSelector(selectAvailableFacilities);
  const canSwitchFacility = availableFacilities && availableFacilities.length > 1;

  // Filter sidebar items based on user permissions
  const filteredSidebarItems = useMemo(() => {
    return sidebarItems.map(item => {
      // Filter Communication submenu based on twoWayTextEnabled
      if (item.path === '/communication' && item.subMenu) {
        return {
          ...item,
          subMenu: item.subMenu.filter(subItem => {
            // Hide Text submenu if twoWayTextEnabled is false
            if (subItem.path === '/communication/text' && !twoWayTextEnabled) {
              return false;
            }
            return true;
          })
        };
      }
      return item;
    });
  }, [twoWayTextEnabled]);

  // Save state to encrypted localStorage whenever it changes
  useEffect(() => {
    secureStorage.setItem(AuthStoreKeys.SidebarIsOpen, isOpen);
  }, [isOpen]);


  useEffect(() => {
    secureStorage.setItem(AuthStoreKeys.SidebarUserHasToggled, userHasToggled);
  }, [userHasToggled]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      console.log('Sidebar: Resize event - width:', width, 'userHasToggled:', userHasToggled);
      
      if (!userHasToggled) {
        // User hasn't manually toggled - use default responsive behavior
        if (width >= 1024) {
          setIsOpen(true);
        } else if (width < 768) {
          setIsOpen(true);
        } else {
          setIsOpen(false);
        }
      } else {
        // User has manually toggled - respect their preference based on screen size
        if (width >= 1024) {
          // Large screens: always expanded when user has toggled
          setIsOpen(true);
        } else if (width < 768) {
          setIsOpen(true);
          // Mobile screens: keep current state, don't auto-change
          // Don't modify isOpen here - let user control it
        } else {
          // Medium screens (768-1023): collapsed state (user's preference)
          setIsOpen(false);
        }
      }
    };

    // Only run initial resize if user hasn't toggled
    if (!userHasToggled) {
      handleResize();
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [userHasToggled]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    setActivePath(currentPath);
  }, []);

  // Listen for route changes to update active path
  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  // Auto-open sub-menu if current route is within a sub-menu item
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Find if current path matches any sub-menu item
    for (const item of filteredSidebarItems) {
      if (item.subMenu) {
        const isSubMenuActive = item.subMenu.some(subItem => 
          currentPath === subItem.path || currentPath.startsWith(subItem.path + '/')
        );
        
        if (isSubMenuActive) {
          setOpenSubMenu(item.path);
          return;
        }
      }
    }
    
    // If no sub-menu matches, close all sub-menus
    // But keep open if we're on a parent path that has a sub-menu
    const hasActiveParent = filteredSidebarItems.some(item => 
      item.subMenu && currentPath.startsWith(item.path)
    );

    if (!hasActiveParent) {
      setOpenSubMenu(null);
    }
  }, [location.pathname, filteredSidebarItems]);

  // Auto-collapse for specific pages (Communication, Reminders, etc.)
  useEffect(() => {
    const currentPath = location.pathname;
    let shouldCollapse = false;

    // Check main menu items and their submenus
    for (const item of filteredSidebarItems) {
      // Check if current path matches main menu item
      if (currentPath === item.path || currentPath.startsWith(item.path + '/')) {
        if (shouldCollapseOnNavigation(item)) {
          shouldCollapse = true;
          break;
        }
      }
      
      // Check submenu items
      if (item.subMenu) {
        const matchingSubItem = item.subMenu.find(subItem => 
          currentPath === subItem.path || currentPath.startsWith(subItem.path + '/')
        );
        if (matchingSubItem && shouldCollapseOnNavigation(matchingSubItem)) {
          shouldCollapse = true;
          break;
        }
      }
    }
    
    if (shouldCollapse) {
      setIsOpen(false);
    }
  }, [location.pathname, filteredSidebarItems]);

  const toggleSidebar = () => {
    setUserHasToggled(true); // Mark that user has manually toggled
    
    // Toggle expanded/collapsed for all screen sizes
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
  };

  const handleNavigation = (item: SidebarItem, isSubMenuItem: boolean = false) => {
    // Check if navigation is blocked before navigating
    checkAndNavigate(() => {
      setActivePath(item.path);
      navigate(item.path);
      
      // Close sub-menu when navigating to a non-sub-menu item
      if (!isSubMenuItem && !item.subMenu) {
        setOpenSubMenu(null);
      }
      
      if (shouldCollapseOnNavigation(item)) {
        setIsOpen(false);
      } else {
        if ((!(window.innerWidth >= 768) && !(window.innerWidth < 1024))|| (window.innerWidth >= 1024)) {
          setIsOpen(true);
        }
      }
      
      // Auto-close mobile sidebar after navigation
      if (window.innerWidth < 768 && onMobileNavigation) {
        onMobileNavigation();
      }
    });
  };

  const handleSubMenuNavigation = (subItem: SubMenuItem) => {
    // Check if navigation is blocked before navigating
    checkAndNavigate(() => {
      setActivePath(subItem.path);
      navigate(subItem.path);
      
      if (shouldCollapseOnNavigation(subItem)) {
        setIsOpen(false);
      }
      
      // Auto-close mobile sidebar after navigation
      if (window.innerWidth < 768 && onMobileNavigation) {
        onMobileNavigation();
      }
    });
  };

  const toggleSubMenu = (itemPath: string) => {
    setOpenSubMenu(prevOpen => prevOpen === itemPath ? null : itemPath);
  };

  const handleFacilityClick = () => {
    if (canSwitchFacility) {
      setShowFacilityModal(true);
    }
  };

  const handleFacilitySwitch = (index: number) => {
    // Check if navigation is blocked before switching facility
    checkAndNavigate(() => {
      dispatch(selectFacility(index));
      setShowFacilityModal(false);
      
      // Clear all React Query cache to remove old facility data
      queryClient.clear();
      
      // Navigate to dashboard to start fresh with new facility
      navigate('/dashboard', { replace: true });
    });
  };

  return (
    <div
      className={`bg-white shadow-lg flex flex-col transition-all duration-300 ease-in-out shrink-0 relative z-[80] ${
        isOpen || window.innerWidth < 768
          ? 'w-[var(--sidebar-width-expanded)]'
          : 'w-[var(--sidebar-width-collapsed)]'
      } ${className}`}
    >
      {/* Logo and Menu Toggle Section */}
      <div className={`flex items-center select-none ${isOpen ? 'justify-between px-5 py-[10px]' : 'justify-center py-[10px]'}`}>
        {isOpen && (
          <img src="/images/img_logo_1.png" alt="logo" className="w-[138px] h-[26px]" />
        )}
        <button 
          onClick={toggleSidebar}
          className="w-[23px] h-8 bg-transparent rounded-sm-plus items-center justify-center hover:bg-gray-100 transition-colors hidden md:flex"
        >
          {isOpen ? (
            <img src="/images/img_text_indent_left.svg" alt="menu" className="w-[22px] h-8 transition-transform duration-100 hover:scale-x-[-1] hover:w-[23px]" />
          ) : (
            <img src="/images/img_text_indent_left.svg" alt="menu" className="w-[22px] h-8 scale-x-[-1] transition-transform duration-100 hover:scale-x-[1] hover:w-[23px]" />
          )}
        </button>
      </div>

      {/* User Profile Section */}
      <div className={`flex flex-col items-center ${isOpen ? 'px-5 py-6 pt-3' : 'px-2 py-4'}`}>
        <Avatar className="h-[72px] w-[72px] mb-3">
          <AvatarImage src={userProfileImage || undefined} alt="profile" />
          <AvatarFallback className='text-2xl' id={authUserId?.toString()}>{getInitials(fullName)}</AvatarFallback>
        </Avatar>
        {isOpen && (
          <>
            <h3 className="text-base font-semibold text-ink-soft mb-1.5 break-all">{fullName}</h3>
            <p className="text-sm text-hh-primary mb-1.5">{userTypeLabel}</p>
            
            {/* Facility Dropdown */}
            <div 
              className="flex items-center gap-1 text-hh-primary cursor-pointer hover:text-ink-soft transition-colors"
              onClick={handleFacilityClick}
            >
              <span className="text-sm">{practiceInfo?.practiceName}</span>
              {canSwitchFacility && (
                <i className="bi bi-caret-down-fill text-sm flex text-ink-soft"></i>
              )}
            </div>
          </>
        )}
      </div>

      {/* Navigation Items */}
      <div className={`flex flex-col ${isOpen ? 'px-3 pb-4' : 'px-2 pb-4'} flex-1 overflow-auto select-none`}>
        {filteredSidebarItems.map((item, index) => {
          // Check if this item or its sub-items are active (prefix match so /settings/profile highlights Settings)
          const isParentActive =
            activePath === item.path || activePath.startsWith(`${item.path}/`);
          const isSubMenuItemActive = item.subMenu?.some(subItem => 
            activePath === subItem.path || activePath.startsWith(subItem.path + '/')
          );
          const isActive = isParentActive || isSubMenuItemActive;
          
          // If item has sub-menu
          if (item.subMenu) {
            // When sidebar is OPEN - show expandable submenu
            if (isOpen) {
              return (
                <Collapsible
                  key={index}
                  open={openSubMenu === item.path}
                  onOpenChange={() => toggleSubMenu(item.path)}
                >
                  <CollapsibleTrigger asChild>
                    <div 
                      title={item.label}
                      className={`flex items-center gap-3 px-3 py-3 rounded-sm-plus mb-1 cursor-pointer transition-all duration-200 relative tab ${
                        isActive ? 'bg-neutral-300/30' : 'text-ink-muted'
                      }`}
                    >
                      <span className="notch"></span>
                      <div className="w-[20px] h-[20px] flex items-center justify-center ml-0.5">
                        <img 
                          src={item.icon} 
                          alt={item.alt} 
                          className="w-[20px] h-[20px] shrink-0"
                        />
                      </div>
                      <span className="text-[15px] font-medium text-ink-soft ml-[6px] flex-1 truncate">
                        {item.label}
                      </span>
                      <i className={`bi ${openSubMenu === item.path ? 'bi-caret-up-fill' : 'bi-caret-down-fill'} text-xs text-ink-muted transition-transform duration-200`}></i>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="ml-2 mb-[6px] pl-3.5 border-l border-divider">
                    {item.subMenu.map((subItem, subIndex) => {
                      const isSubItemActive = activePath === subItem.path || activePath.startsWith(subItem.path + '/');
                      return (
                        <div
                          key={subIndex}
                          title={subItem.label}
                          onClick={() => handleSubMenuNavigation(subItem)}
                          className={`flex items-center px-3 py-2.5 rounded-sm-plus mb-1 cursor-pointer transition-all duration-200 relative tab ${
                            isSubItemActive 
                              ? 'bg-[#334eff62] text-ink-soft' 
                              : 'text-ink-muted hover:bg-gray-200'
                          }`}
                        >
                          <span className={`notch ${isSubItemActive ? 'bg-hh-primary' : 'bg-white'}`}></span>
                          <div className="w-[18px] h-[18px] flex items-center justify-center ml-0.5">
                            <img 
                              src={subItem.icon} 
                              alt={subItem.alt} 
                              className="w-[18px] h-[18px]"
                            />
                          </div>
                          <span className="text-sm font-medium ml-5">
                            {subItem.label}
                          </span>
                        </div>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              );
            } else {
              // When sidebar is COLLAPSED - show popover on click
              const isPopoverOpen = openPopover === item.path;
              return (
                <Popover 
                  key={index} 
                  open={isPopoverOpen}
                  onOpenChange={(open) => setOpenPopover(open ? item.path : null)}
                >
                  <PopoverTrigger asChild>
                    <div
                      title={item.label}
                      className={`w-full h-[48px] rounded-sm-plus flex items-center justify-center mb-1 shrink-0 cursor-pointer transition-all duration-200 relative tab ${
                        isActive ? 'bg-[#334eff62]' : 'text-ink-muted hover:bg-gray-100'
                      }`}
                    >
                      <span className={`notch ${isActive ? 'bg-hh-primary' : 'bg-white'}`}></span>
                      <div className="w-[20px] h-[20px] flex items-center justify-center">
                        <img 
                          src={item.icon} 
                          alt={item.alt} 
                          className="w-[20px] h-[20px]"
                        />
                      </div>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent 
                    side="right" 
                    align="start"
                    sideOffset={8}
                    className="w-56 p-2 z-[100]"
                  >
                    <div className="space-y-1">
                      {/* Parent menu label */}
                      <div className="px-3 py-2 text-sm font-semibold text-gray-700 border-b border-gray-200 mb-2">
                        {item.label}
                      </div>
                      
                      {/* Submenu items */}
                      {item.subMenu.map((subItem, subIndex) => {
                        const isSubItemActive = activePath === subItem.path || activePath.startsWith(subItem.path + '/');
                        return (
                          <div
                            key={subIndex}
                            onClick={() => {
                              handleSubMenuNavigation(subItem);
                              setOpenPopover(null);
                            }}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-sm-plus cursor-pointer transition-all duration-200 relative tab ${
                              isSubItemActive 
                                ? 'bg-[#334eff62] text-ink-soft' 
                                : 'text-ink-muted hover:bg-gray-100'
                            }`}
                          >
                            <span className={`notch ${isSubItemActive ? 'bg-hh-primary' : 'bg-white'}`}></span>
                            <div className="w-[18px] h-[18px] flex items-center justify-center">
                              <img 
                                src={subItem.icon} 
                                alt={subItem.alt} 
                                className="w-[18px] h-[18px]"
                              />
                            </div>
                            <span className="text-sm font-medium">
                              {subItem.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            }
          }
          
          // Regular menu item without sub-menu
          return (
            <div 
              title={item.label}
              key={index}
              onClick={() => handleNavigation(item)}
              className={`${
                isOpen 
                  ? 'flex items-center gap-3 px-3 py-3 rounded-sm-plus mb-1' 
                  : 'w-full h-[48px] rounded-sm-plus flex items-center justify-center mb-1 shrink-0'
              } cursor-pointer transition-all duration-200 relative tab ${
                isActive ? 'bg-[#334eff62]' : 'text-ink-muted'
              }`}
            >
              <span className={`notch ${isActive ? 'bg-hh-primary' : 'bg-white'}`}></span>
              <div className="w-[20px] h-[20px] flex items-center justify-center ml-0.5">
                <img 
                  src={item.icon} 
                  alt={item.alt} 
                  className="w-[20px] h-[20px] shrink-0"
                />
              </div>
              {isOpen && (
                <span className="text-[15px] font-medium text-ink-soft ml-[6px] truncate">
                  {item.label}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Facility Switcher Dialog */}
      {canSwitchFacility && availableFacilities && (
        <FacilitySwitcherDialog
          isOpen={showFacilityModal}
          onClose={() => setShowFacilityModal(false)}
          facilities={availableFacilities}
          currentPracticeId={practiceInfo?.id || 0}
          onSwitch={handleFacilitySwitch}
        />
      )}
    </div>
  );
};

export default Sidebar;
